<template>
  <div class="total-reflection-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$θ_c = arcsin(n₂ / n₁)$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 13" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="400" />
              <line v-for="i in 9" :key="'h'+i" x1="0" :y1="i*50" x2="600" :y2="i*50" />
           </g>

           <!-- 界面: 空气 (疏) vs 水/玻璃 (密) -->
           <rect x="0" y="0" width="600" height="200" fill="#0f172a" /> <!-- 空气 -->
           <rect x="0" y="200" width="600" height="200" fill="rgba(56, 189, 248, 0.15)" /> <!-- 密介质 -->
           
           <line x1="0" y1="200" x2="600" y2="200" stroke="#38bdf8" stroke-width="3" />
           <line x1="300" y1="50" x2="300" y2="350" stroke="#94a3b8" stroke-width="2" stroke-dasharray="8,6" />

           <!-- 标识文字 -->
           <text x="50" y="40" fill="#94a3b8" font-size="16" font-weight="900">疏介质 (空气 n₂ = {{ n2.toFixed(2) }})</text>
           <text x="50" y="360" fill="#38bdf8" font-size="16" font-weight="900">密介质 (n₁ = {{ n1.toFixed(2) }})</text>

           <!-- 角度辅助弧线段 -->
           <path :d="angleArc(300, 200, 40, rad, Math.PI)" fill="rgba(250, 204, 21, 0.3)" />
           <text :x="300 - Math.sin(rad/2)*60" :y="200 + Math.cos(rad/2)*60" fill="#facc15" font-size="14" text-anchor="middle">i</text>

           <!-- 光束渲染 -->
           <g transform="translate(300, 200)">
              <!-- 入射光 (从下方密介质向上) -->
              <line :x1="-Math.sin(rad)*150" :y1="Math.cos(rad)*150" x2="0" y2="0" stroke="#facc15" stroke-width="4" stroke-linecap="round" />
              <!-- 光源发射器 -->
              <circle :cx="-Math.sin(rad)*150" :cy="Math.cos(rad)*150" r="10" fill="#1e293b" stroke="#facc15" stroke-width="3" />
              
              <!-- 发生折射 -->
              <template v-if="!isTotalReflection">
                 <line 0 y1="0" :x2="Math.sin(refractedRad)*150" :y2="-Math.cos(refractedRad)*150" stroke="#facc15" stroke-width="3" stroke-linecap="round" />
                 <!-- 少量微弱的反射光 -->
                 <line 0 y1="0" :x2="Math.sin(rad)*150" :y2="Math.cos(rad)*150" stroke="rgba(250, 204, 21, 0.3)" stroke-width="2" />
                 <!-- 折射角弧线 -->
                 <path :d="angleArc(0, 0, 40, 0, refractedRad)" fill="rgba(56, 189, 248, 0.3)" />
                 <text :x="Math.sin(refractedRad/2)*60" :y="-Math.cos(refractedRad/2)*60" fill="#38bdf8" font-size="14" text-anchor="middle">r</text>
              </template>
              
              <!-- 发生全反射 -->
              <template v-else>
                 <line 0 y1="0" :x2="Math.sin(rad)*150" :y2="Math.cos(rad)*150" stroke="#facc15" stroke-width="4" stroke-linecap="round" filter="url(#glow-light)" />
              </template>
           </g>

           <!-- 临界角辅助标记 -->
           <line x1="300" y1="200" :x2="300 - Math.sin(criticalRad)*200" :y2="200 + Math.cos(criticalRad)*200" stroke="#f87171" stroke-width="1.5" stroke-dasharray="4,4" />
           <text :x="300 - Math.sin(criticalRad)*210" :y="200 + Math.cos(criticalRad)*210" fill="#f87171" font-size="12">临界角 C</text>

           <text v-if="isTotalReflection" x="420" y="80" fill="#f43f5e" font-size="28" font-weight="900" style="text-shadow: 0 4px 6px rgba(0,0,0,0.5)">全反射现象</text>
        </svg>

        <defs>
           <filter id="glow-light">
             <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
             <feMerge>
                <feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/>
             </feMerge>
           </filter>
        </defs>

      </div>

      <!-- 右侧实验数据看板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">光学定律解析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">斯涅尔定律与临界条件</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>当前入射角 i</span><span class="hl text-blue">{{ angle.toFixed(1) }}°</span></div>
                  <div class="data-row"><span>介质比值 (n₂/n₁)</span><span class="hl">{{ (n2/n1).toFixed(3) }}</span></div>
                  <div class="data-row"><span>临界角 C = arcsin(n₂/n₁)</span><span class="hl text-red">{{ criticalAngle.toFixed(1) }}°</span></div>
                  <div class="data-row" :style="{ background: isTotalReflection ? 'rgba(244, 63, 94, 0.15)' : 'transparent' }">
                     <span>折射角 r</span>
                     <span class="hl" :class="isTotalReflection ? 'text-red' : 'text-green'">{{ isTotalReflection ? '无折射光' : (refractedRad*180/Math.PI).toFixed(1)+'°' }}</span>
                  </div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>当光从光密介质（折射率大）射入光疏介质（折射率小）时，折射角会大于入射角。随着入射角不断增大，折射角会先一步达到 90° 并平行于界面。如果入射角继续增大（超过<strong>临界角</strong>），折射光线将彻底消失，所有的光能都会被反射回原介质内，这就是全反射。利用此原理制造了光纤，实现信息无损传输。</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>拖动改变激光入射角 (i)</label>
               <el-slider v-model="angle" :min="0" :max="85" :step="0.5" size="small"></el-slider>
            </div>
            
            <div class="s-item">
               <label>密介质折射率 (n₁)</label>
               <el-select v-model="n1" size="small">
                  <el-option label="水 (n ≈ 1.33)" :value="1.33"></el-option>
                  <el-option label="普通玻璃 (n ≈ 1.50)" :value="1.50"></el-option>
                  <el-option label="金刚石 (n ≈ 2.42)" :value="2.42"></el-option>
               </el-select>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TotalReflectionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      n1: 1.5,
      n2: 1.0,
      angle: 30
    }
  },
  computed: {
    topicIntro() {
       return '全反射与临界角：光逃不出的“密介质牢笼”';
    },
    rad() { return this.angle * Math.PI / 180; },
    criticalRad() { return Math.asin(this.n2 / this.n1); },
    criticalAngle() { return this.criticalRad * 180 / Math.PI; },
    isTotalReflection() { return this.angle >= this.criticalAngle; },
    refractedRad() {
       if (this.isTotalReflection) return 0;
       return Math.asin((this.n1 * Math.sin(this.rad)) / this.n2);
    }
  },
  methods: {
    angleArc(cx, cy, r, angleStart, angleEnd) {
       // helper for drawing small angle arcs
       // Using simpler mapping for incident and refracted relative to normal
       let startX = cx - Math.sin(angleStart)*r;
       let startY = cy + Math.cos(angleStart)*r;
       // if we assume angleEnd is the normal (0 rad effectively)
       return `M ${cx} ${cy+r} A ${r} ${r} 0 0 1 ${startX} ${startY}`;
    }
  }
}
</script>

<style scoped lang="scss">
.total-reflection-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 16px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
